import { useState } from "react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { getSignupPicks, deleteSignupPick, createSignupPick, updateSignupPick } from "../../lib/api";

/**
 * Compact button that opens a popover with the full sign-up roster + admin remove actions.
 * Works for both block options (date required) and trips (date null; tripDate scopes attendance).
 */
export default function SignupRosterPopover({
  option,        // { id, title, lead_staff_name, location, capacity, slot_id, type }
  date,          // YYYY-MM-DD for blocks, null for trips
  tripDate,      // for trips: the specific date column being managed (per-date attendance)
  studentCount,  // initial summary count
  studentNames = [],
  students = [], // full student list for inline add
  supportStaffNames = [], // first names of support staff (trips)
  tone = "indigo", // indigo | rose | amber
  readOnly = false, // when true, hide add/remove/move actions (view only)
  onChanged,
}) {
  const [open, setOpen] = useState(false);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [addQuery, setAddQuery] = useState("");

  const TONES = {
    rose: { text: "text-rose-900", subtle: "text-rose-600", chip: "bg-rose-100 text-rose-700 hover:bg-rose-200", head: "bg-rose-50" },
    amber: { text: "text-amber-900", subtle: "text-amber-700", chip: "bg-amber-100 text-amber-800 hover:bg-amber-200", head: "bg-amber-50" },
    indigo: { text: "text-indigo-900", subtle: "text-indigo-600", chip: "bg-indigo-100 text-indigo-700 hover:bg-indigo-200", head: "bg-indigo-50" },
  };
  const colors = TONES[tone] || TONES.indigo;

  const fetchRoster = async () => {
    setLoading(true);
    try {
      const params = { option_id: option.id };
      if (date) params.date = date;
      const res = await getSignupPicks(params);
      // For trips managed per-date, only show students attending this specific date.
      let rows = res.data;
      if (tripDate) {
        rows = rows.filter((p) => !(p.attend_dates && p.attend_dates.length) || p.attend_dates.includes(tripDate));
      }
      setRoster(rows);
    } catch {
      toast.error("Failed to load roster");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (p) => {
    const attend = p.attend_dates || [];
    // Multi-date trip: removing from one date should keep the other dates.
    if (tripDate && attend.length > 1) {
      if (!window.confirm(`Remove ${p.student_name} from "${option.title}" on this date only?`)) return;
      setBusyId(p.id);
      try {
        await updateSignupPick(p.id, { attend_dates: attend.filter((d) => d !== tripDate) });
        setRoster((r) => r.filter((x) => x.id !== p.id));
        toast.success("Removed from this date");
        onChanged && onChanged();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to remove");
      } finally {
        setBusyId(null);
      }
      return;
    }
    if (!window.confirm(`Remove ${p.student_name} from "${option.title}"?`)) return;
    setBusyId(p.id);
    try {
      await deleteSignupPick(p.id);
      setRoster((r) => r.filter((x) => x.id !== p.id));
      toast.success("Pick removed");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    } finally {
      setBusyId(null);
    }
  };

  const handleAdd = async (student) => {
    setBusyId("add");
    const payload = { option_id: option.id, student_id: student.id, response: "yes" };
    if (date) payload.date = date;
    if (tripDate) payload.attend_dates = [tripDate];
    try {
      try {
        await createSignupPick(payload);
      } catch (e) {
        if (e.response?.status === 409) {
          if (!window.confirm((e.response?.data?.detail || "At capacity.") + "\n\nAdd anyway?")) { setBusyId(null); return; }
          await createSignupPick({ ...payload, override_capacity: true });
        } else throw e;
      }
      setAddQuery("");
      toast.success(`Added ${student.preferred_name || student.first_name}`);
      await fetchRoster();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally {
      setBusyId(null);
    }
  };

  const rosterIds = new Set(roster.map((p) => p.student_id));
  const isInitial = (n) => (n || "").replace(/[^A-Za-z0-9]/g, "").length <= 1;
  const nameById = {};
  (students || []).forEach((s) => { nameById[s.id] = { first: (s.preferred_name || s.first_name || "").trim(), last: (s.last_name || "").trim() }; });
  // Prefer the live record (from students prop), then backend-enriched fields, then stored name.
  const displayName = (p) => {
    const rec = nameById[p.student_id];
    const parts = (p.student_name || "").trim().split(/\s+/);
    const first = (rec?.first || p.student_first || parts[0] || "").trim();
    const last = (rec?.last || p.student_last || parts.slice(1).join(" ") || "").trim();
    if (isInitial(first) && last) return `${first} ${last}`;
    return first || "Unknown";
  };
  const sortedRoster = [...roster].sort((a, b) => displayName(a).toLowerCase().localeCompare(displayName(b).toLowerCase()));
  const staffGoing = [option.lead_staff_name, ...(supportStaffNames || [])].filter(Boolean);
  const addMatches = addQuery.trim()
    ? students
        .filter((s) => !rosterIds.has(s.id))
        .filter((s) => `${s.preferred_name || s.first_name} ${s.last_name}`.toLowerCase().includes(addQuery.trim().toLowerCase()))
        .slice(0, 6)
    : [];

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) fetchRoster(); }}>
      <PopoverTrigger asChild>
        <button
          className={`text-left leading-tight w-full rounded px-1 py-0.5 transition-colors ${open ? colors.chip : "hover:bg-white/60"}`}
          data-testid={`signup-popover-trigger-${option.id}-${date || "trip"}`}
        >
          <div className={colors.text}>
            <span className="font-semibold text-[11px]">{option.title}</span>
            {staffGoing.length > 0 && <span className={`${colors.subtle} text-[10px]`}> · {staffGoing.join(", ")}</span>}
          </div>
          <div className="flex items-center gap-1">
            <Badge variant="outline" className={`${colors.chip} text-[9px] px-1 py-0 border-0`}>
              {studentCount}{option.capacity != null && ` / ${option.capacity}`}
            </Badge>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0" data-testid={`signup-popover-content-${option.id}`}>
        <div className={`px-3 py-2 border-b ${colors.head}`}>
          <div className={`font-semibold text-sm ${colors.text}`}>{option.title}</div>
          <div className="text-xs text-muted-foreground space-x-1">
            {staffGoing.length > 0 && <span>Staff: {staffGoing.join(", ")}</span>}
            {option.location && <span>· {option.location}</span>}
            {option.capacity != null && <span>· Cap {studentCount}/{option.capacity}</span>}
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {loading && <div className="px-3 py-3 text-xs text-muted-foreground">Loading…</div>}
          {!loading && roster.length === 0 && (
            <div className="px-3 py-3 text-xs text-muted-foreground italic">
              {studentNames.length > 0
                ? studentNames.map((n, i) => <div key={i} className="text-gray-700 not-italic py-0.5">{i + 1}. {n}</div>)
                : "No students yet"}
            </div>
          )}
          {!loading && sortedRoster.map((p, i) => (
            <div
              key={p.id}
              className="flex items-center justify-between gap-2 px-3 py-1.5 hover:bg-gray-50 group"
              data-testid={`signup-roster-row-${p.id}`}
            >
              <div className="flex items-baseline gap-2 min-w-0 flex-1">
                <span className="text-xs text-muted-foreground tabular-nums w-5">{i + 1}.</span>
                <span className="text-sm truncate">{displayName(p)}</span>
                {p.response && p.response !== "yes" && (
                  <Badge variant="outline" className="text-[9px] px-1 py-0">{p.response.toUpperCase()}</Badge>
                )}
                {p.over_capacity_at_pick && (
                  <Badge variant="outline" className="text-[9px] px-1 py-0 bg-amber-50 text-amber-800 border-amber-200">⚠ Over</Badge>
                )}
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {!readOnly && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[10px] text-red-600"
                  onClick={() => handleRemove(p)}
                  disabled={busyId === p.id}
                  data-testid={`signup-roster-remove-${p.id}`}
                >
                  {busyId === p.id ? "..." : "Remove"}
                </Button>
                )}
              </div>
            </div>
          ))}
        </div>
        {!readOnly && students.length > 0 && (
          <div className="border-t p-2 bg-gray-50/60" data-testid={`signup-add-wrap-${option.id}`}>
            <input
              className="w-full text-sm border rounded px-2 py-1"
              placeholder="+ Add student…"
              value={addQuery}
              onChange={(e) => setAddQuery(e.target.value)}
              disabled={busyId === "add"}
              data-testid={`signup-add-input-${option.id}`}
            />
            {addMatches.length > 0 && (
              <div className="mt-1 max-h-40 overflow-y-auto border rounded bg-white">
                {addMatches.map((s) => (
                  <button
                    key={s.id}
                    className="w-full text-left text-sm px-2 py-1.5 hover:bg-indigo-50 disabled:opacity-50"
                    onClick={() => handleAdd(s)}
                    disabled={busyId === "add"}
                    data-testid={`signup-add-option-${option.id}-${s.id}`}
                  >
                    {(s.preferred_name || s.first_name)} {s.last_name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
